import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchTranslation } from "../../redux/Slices/translationDataSlice";
import { useLangPath, useLang } from "../../hooks/useLangPath";
import { LangCode } from "../../constants/route-slugs";
import MobileAppSection from "../../components/pages/home/mobile-app-section/MobileAppSection";
import TitleUpdater from "../../components/shared/title/TitleUpdater";
import styles from "./Services.module.scss";

interface ServiceItem {
  slugs: Record<LangCode, string>;
  titleKey: keyof import("../../interface/site.interface").TranslationKeys;
  fallbackTitle: string;
  icon: React.ReactElement;
}

const SERVICE_ITEMS: ServiceItem[] = [
  {
    slugs: { az: "baglamalarin-dasinmasi", en: "packages-transportation", ru: "perevozka-bagazha" },
    titleKey: "services_packages_transportation",
    fallbackTitle: "Bağlamaların Daşınması",
    icon: (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="20" width="48" height="36" rx="4" stroke="#36654a" strokeWidth="2.5" fill="#e8f5ee"/>
        <path d="M22 20V14a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v6" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M8 32h48" stroke="#36654a" strokeWidth="2.5"/>
        <rect x="26" y="28" width="12" height="8" rx="2" fill="#36654a"/>
      </svg>
    ),
  },
  {
    slugs: { az: "kuryer-sifarisi", en: "courier-order", ru: "zakaz-kuriera" },
    titleKey: "services_courier_order",
    fallbackTitle: "Kuryer Sifarişi",
    icon: (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="22" width="40" height="26" rx="4" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M44 32h8l8 10v6h-16V32z" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <circle cx="16" cy="52" r="5" fill="#fff" stroke="#36654a" strokeWidth="2.5"/>
        <circle cx="48" cy="52" r="5" fill="#fff" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M14 34h16" stroke="#36654a" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M14 40h10" stroke="#36654a" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    slugs: { az: "poct-sifarisi", en: "post-order", ru: "pochtovyy-zakaz" },
    titleKey: "services_post_order",
    fallbackTitle: "Poçt Sifarişi",
    icon: (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="6" y="16" width="52" height="36" rx="4" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M6 20l26 18L58 20" stroke="#36654a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    slugs: { az: "musteri-xidmetleri", en: "customer-services", ru: "obsluzhivanie-klientov" },
    titleKey: "services_customer_services",
    fallbackTitle: "Müştəri Xidmətləri",
    icon: (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="32" cy="20" r="12" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M10 54c0-12.15 9.85-22 22-22s22 9.85 22 22" stroke="#36654a" strokeWidth="2.5" strokeLinecap="round"/>
        <circle cx="32" cy="20" r="5" fill="#36654a"/>
      </svg>
    ),
  },
  {
    slugs: { az: "korporativ", en: "corporate", ru: "korporativnyy" },
    titleKey: "corporate_title",
    fallbackTitle: "Korporativ",
    icon: (
      <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="8" y="20" width="48" height="36" rx="4" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <rect x="24" y="8" width="16" height="12" rx="2" fill="#e8f5ee" stroke="#36654a" strokeWidth="2.5"/>
        <path d="M8 34h48" stroke="#36654a" strokeWidth="2.5"/>
        <rect x="28" y="40" width="8" height="16" fill="#36654a" rx="1"/>
      </svg>
    ),
  },
];

const Services = () => {
  const { data } = useSelector((state: RootState) => state.translationData);
  const language = useSelector((state: RootState) => state.language.language);
  const dispatch = useDispatch<AppDispatch>();
  const initialRender = useRef(true);
  const lp = useLangPath();
  const lang = useLang();

  const servicesSlug = lp("services").replace(`/${lang}/`, "");

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) dispatch(fetchTranslation());
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  return (
    <div id={styles.services_page}>
      <TitleUpdater
        title={`${data?.colibri || "Colibri"} | ${data?.services_title || "Xidmətlər"}`}
      />
      <div className="container">
        <div className={styles.services}>
          <h1 className={styles.services_title}>
            {data?.services_title || "Xidmətlər"}
          </h1>
          <div className={styles.services_grid}>
            {SERVICE_ITEMS.map((service) => {
              const slug = service.slugs[lang] ?? service.slugs.az;
              return (
                <Link
                  key={slug}
                  to={`/${lang}/${servicesSlug}/${slug}`}
                  className={styles.service_card}
                >
                  <div className={styles.card_icon}>{service.icon}</div>
                  <p className={styles.card_title}>{(data?.[service.titleKey] as string) || service.fallbackTitle}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
      <MobileAppSection />
    </div>
  );
};

export default Services;
